"use client";

import { useState } from "react";
import { useTranslations } from "@zapier/i18n";
import { Badge, ServiceIconShell, Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { RelativeTime } from "@zapier/date-am";
import { AssetTypes, getAssetIcon } from "@zapier/utils";
import type { HydratedAsset } from "@zapier/client";

import styles from "./AssetsSelectedCard.module.css";

interface Props {
  count: number;
  assets: HydratedAsset[];
}

/**
 * Collapsible card listing the assets selected for cross-account copy.
 * Defaults to collapsed since the count alone is enough confirmation in
 * the common case.
 */
export function AssetsSelectedCard({ count, assets }: Props) {
  const [expanded, setExpanded] = useState(false);
  const t = useTranslations("copy-to-another-account-modal");
  const noAssetsSelectedLabel = t("review-step.no-assets-selected");
  const isEmpty = count === 0;

  return (
    <div className={expanded ? styles.cardExpanded : styles.card}>
      {isEmpty ? (
        <div className={styles.headerStatic}>
          <span className={styles.headerLabel}>
            <Text type="Body2" tag="span" className={styles.title}>
              {noAssetsSelectedLabel}
            </Text>
          </span>
          <Badge variant="neutral">{String(count)}</Badge>
        </div>
      ) : (
        <button
          type="button"
          className={styles.header}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <span className={styles.headerLabel}>
            <Icon
              name={expanded ? "arrowSmallDown" : "arrowSmallRight"}
              size={24}
            />
            <Text type="Body2" tag="span" className={styles.title}>
              {t("review-step.assets-selected")}
            </Text>
          </span>
          <Badge variant="neutral">{String(count)}</Badge>
        </button>
      )}
      {expanded && !isEmpty ? (
        <div className={styles.list}>
          {assets.map((asset) => (
            <AssetRow key={String(asset.id)} asset={asset} />
          ))}
        </div>
      ) : undefined}
    </div>
  );
}

function AssetRow({ asset }: { asset: HydratedAsset }) {
  const t = useTranslations("copy-to-another-account-modal");
  const tFallback = useTranslations("fallbacks");
  const name = getAssetDisplayName(asset, tFallback);
  const lastModified = getAssetLastModifiedDate(asset);
  return (
    <div className={styles.row}>
      <ServiceIconShell size="medium">
        <Icon name={getAssetIcon(asset.type)} size={20} color="BrandOrange" />
      </ServiceIconShell>
      <div className={styles.info}>
        <Text type="SmallPrint1" tag="div" className={styles.name}>
          {name}
        </Text>
        {lastModified ? (
          <Text type="MinimalPrint1" color="TextWeakest" tag="div">
            {t.rich("review-step.updated-ago", {
              time: () => <RelativeTime date={lastModified} />,
            })}
          </Text>
        ) : undefined}
      </div>
    </div>
  );
}

/**
 * Different hydrated asset types use different name fields (`title` for zaps,
 * `name` for most others). Use an "Untitled <AssetType>" fallback when no
 * user-provided name exists.
 */
function getAssetDisplayName(
  asset: HydratedAsset,
  tFallback: (key: "untitled-asset", values: { assetType: string }) => string,
): string {
  const candidate = (asset as { title?: unknown }).title;
  if (typeof candidate === "string" && candidate.trim().length > 0) {
    return candidate.trim();
  }
  const nameField = (asset as { name?: unknown }).name;
  if (typeof nameField === "string" && nameField.trim().length > 0) {
    return nameField.trim();
  }
  return getUntitledAssetLabel(asset, tFallback);
}

function getUntitledAssetLabel(
  asset: HydratedAsset,
  tFallback: (key: "untitled-asset", values: { assetType: string }) => string,
): string {
  switch (asset.type) {
    case AssetTypes.Zap: {
      return tFallback("untitled-asset", { assetType: "Zap" });
    }
    case AssetTypes.Table:
    case AssetTypes.TableView:
    case AssetTypes.TableSubscription: {
      // Keep table-derived assets grouped under the user-facing "Table" label.
      return tFallback("untitled-asset", { assetType: "Table" });
    }
    case AssetTypes.Interface: {
      return tFallback("untitled-asset", { assetType: "Form" });
    }
    case AssetTypes.Chatbot: {
      return tFallback("untitled-asset", { assetType: "Chatbot" });
    }
    case AssetTypes.Agent: {
      return tFallback("untitled-asset", { assetType: "Agent" });
    }
    case AssetTypes.Canvas: {
      return tFallback("untitled-asset", { assetType: "Canvas" });
    }
    default: {
      // Keep this generic fallback user-facing and non-internal.
      return tFallback("untitled-asset", { assetType: "" }).trim();
    }
  }
}

/**
 * Returns the "last modified" timestamp for a hydrated asset, handling the
 * per-type field naming:
 *  - Tables & VirtualTables expose `editedAt`
 *  - Everything else (Zap / Interface / Canvas / Agent / Chatbot) exposes
 *    `updatedAt`
 * This matches how folder asset-row components (`AssetRowZap`, `AssetRowTable`,
 * `AssetRowInterface`, etc.) read the same field. Returns `undefined` when no
 * usable value is present so the subtitle hides cleanly.
 */
function getAssetLastModifiedDate(asset: HydratedAsset): Date | undefined {
  const value =
    asset.type === AssetTypes.Table
      ? (asset as { editedAt?: string }).editedAt
      : (asset as { updatedAt?: string }).updatedAt;
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : undefined;
}
